import React from 'react';
import {
  Box,
  Typography,
  Stack,
  Chip,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Article as ArticleIcon,
  Folder as CategoryIcon,
  SearchOff as NoResultsIcon,
} from '@mui/icons-material';
import { useArticleSearch } from '../../hooks/useHelp';

interface SearchResultsProps {
  query: string;
  onSelectArticle: (slug: string) => void;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  query,
  onSelectArticle,
}) => {
  const { data, isLoading, error } = useArticleSearch(query, {
    enabled: query.length >= 2,
  });

  const results = data?.results || [];
  const total = data?.total || 0;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="error">
          Failed to search. Please try again.
        </Typography>
      </Box>
    );
  }

  if (query.length < 2) {
    return (
      <Box textAlign="center" py={4}>
        <Typography color="text.secondary">
          Type at least 2 characters to search
        </Typography>
      </Box>
    );
  }

  if (results.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <NoResultsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          No results found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try different keywords or browse help categories
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" mb={2} display="block">
        {total} result{total !== 1 ? 's' : ''} for "{query}"
      </Typography>

      <Stack spacing={1.5}>
        {results.map((result) => (
          <Paper
            key={result.id}
            onClick={() => result.type === 'article' && onSelectArticle(result.slug)}
            sx={{
              p: 2,
              cursor: result.type === 'article' ? 'pointer' : 'default',
              transition: 'all 0.2s',
              '&:hover': result.type === 'article' ? {
                bgcolor: 'action.hover',
                transform: 'translateX(4px)',
              } : {},
            }}
          >
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              <Box
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  bgcolor: result.type === 'article' ? 'primary.light' : 'secondary.light',
                  color: result.type === 'article' ? 'primary.main' : 'secondary.main',
                  display: 'flex',
                }}
              >
                {result.type === 'article' ? (
                  <ArticleIcon fontSize="small" />
                ) : (
                  <CategoryIcon fontSize="small" />
                )}
              </Box>

              <Box flex={1}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  {result.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  {result.description}
                </Typography>

                <Stack direction="row" spacing={1}>
                  <Chip
                    label={result.type}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem', textTransform: 'capitalize' }}
                  />
                  {result.category && (
                    <Chip
                      label={result.category}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  )}
                  <Chip
                    label={`${Math.round(result.relevance * 100)}% match`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                </Stack>
              </Box>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default SearchResults;
